// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import Button from 'presentations/views/components/atoms/Button';
import { fontSize, fontWeight, spacing } from 'presentations/views/styles';

import { SyntheticEvent, ReactNode } from 'react';
import { gray02, gray05 } from 'presentations/views/styles/color';
import Switch from 'presentations/views/components/atoms/Switch';
import FormLabel from 'presentations/views/components/molecules/FormLabel';
import FormPaidLink from 'presentations/views/components/molecules/FormPaidLink';
import { PlanId } from 'domains/models/subscription/planId';

interface Props {
  onExpand?: any;
  label: string;
  trigger?: 'text' | 'switch' | ReactNode;
  description?: string | ReactNode;
  summary: string | ReactNode | null;
  name: string;
  helperText?: string;
  content: ReactNode;
  submitField?: ReactNode;
  isOpen: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
  isNotRequired?: boolean;
  isPaid?: boolean;
  isShowPaidLink?: boolean;
  plan?: PlanId;
  help?: { text?: string; url: string };
  cancelLabel?: string;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const trigger = props.trigger || 'text';
  let description = null;
  if (props.isPaid && props.isShowPaidLink) {
    description = (
      <PaidLink>
        <FormPaidLink description={props.description} plan={props.plan} />
      </PaidLink>
    );
  } else {
    if (props.description) {
      description = <Description>{props.description}</Description>;
    }
  }
  return (
    <ExpandFormField>
      <Header>
        <div>
          <FormLabel
            isPaid={props.isPaid}
            isShowPaidLink={props.isShowPaidLink}
            isRequired={props.isRequired}
            isNotRequired={props.isNotRequired}
            plan={props.plan}
            help={props.help}
          >
            {props.label}
          </FormLabel>
          {description}
        </div>
        {!props.isDisabled && trigger === 'text' && (
          <ActionButton
            variant="text"
            onClick={(e: SyntheticEvent) => props.onExpand(e, props.name)}
          >
            {props.isOpen ? props.cancelLabel || t('Cancel') : t('Edit')}
          </ActionButton>
        )}
        {!props.isDisabled && trigger === 'switch' && (
          <Switch
            isChecked={props.isOpen}
            onChange={(e: SyntheticEvent) => props.onExpand(e, props.name)}
          />
        )}
        {typeof trigger !== 'string' && trigger}
      </Header>
      <Field>
        {props.isOpen ? (
          <>
            {props.helperText && <Text>{props.helperText}</Text>}
            {props.content}
          </>
        ) : (
          props.summary
        )}
      </Field>
      {props.isOpen && props.submitField && (
        <Submit>{props.submitField}</Submit>
      )}
    </ExpandFormField>
  );
}

const ExpandFormField = styled.div`
  border-bottom: 1px solid ${gray05};
  margin-bottom: ${spacing(32)};
`;
const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;
const ActionButton = styled(Button)``;
const Text = styled.div`
  margin-bottom: ${spacing(8)} 0;
  color: ${gray02};
`;
const Field = styled.div`
  margin: ${spacing(16)} 0 ${spacing(32)};
`;
const Submit = styled.div`
  margin: ${spacing(16)} 0;
`;

export const Description = styled.div`
  font-size: ${fontSize(12)};
  margin-bottom: ${spacing(16)};
`;

const PaidLink = styled.div`
  margin-bottom: ${spacing(16)};
`;
